import { BusinessError } from '@marketing-milk/interfaces'
import {
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  Typography,
} from '@material-ui/core'
import BuildIcon from '@material-ui/icons/Build'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import React, { useState } from 'react'
import { GoogleIcon, MetaIcon } from './icons'
import { getBusinessErrorInfo, Provider, severityColors } from './severity'

export interface ErrorCenterProps {
  errors: BusinessError[]
  // Whether this business is on Facebook Builder (system user) vs. the legacy Authorizer (per-user
  // OAuth) flow — the two need different remediation instructions for the same error flag.
  onPerson: boolean
  onResolve: (error: BusinessError) => {}
}

const ProviderIcon = ({ provider }: { provider: Provider }) =>
  provider === 'meta' ? <MetaIcon /> : <GoogleIcon />

interface ErrorCardProps {
  error: BusinessError
  onPerson: boolean
  isOpen: boolean
  onToggle: () => void
  onResolve: () => void
}

const ErrorCard = ({ error, onPerson, isOpen, onToggle, onResolve }: ErrorCardProps) => {
  const info = getBusinessErrorInfo(error, onPerson)
  const colors = severityColors[info.severity]

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${colors.border}`,
        background: colors.background,
        borderRadius: 4,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={onToggle}
      >
        <IconButton
          size="small"
          style={{ padding: 2 }}
          onClick={e => {
            e.stopPropagation()
            onToggle()
          }}
        >
          {isOpen ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </IconButton>
        <ProviderIcon provider={info.provider} />
        <div style={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" style={{ color: colors.text, fontWeight: 600 }}>
            {info.title}
          </Typography>
          <Typography variant="body2" style={{ color: colors.text }}>
            {info.summary}
          </Typography>
        </div>
        <Chip
          size="small"
          label={info.severity === 'critical' ? 'Critical' : 'Warning'}
          style={{
            backgroundColor: colors.border,
            color: '#fff',
            fontWeight: 600,
          }}
        />
        <Button
          variant="contained"
          color="default"
          onClick={e => {
            e.stopPropagation()
            onResolve()
          }}
        >
          <BuildIcon className="mr-2" /> Clear
        </Button>
      </div>
      <Collapse in={isOpen} unmountOnExit>
        <div style={{ padding: '0 16px 16px 52px' }}>
          <Typography variant="subtitle2" style={{ color: colors.text, marginBottom: 4 }}>
            How to fix this
          </Typography>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {info.steps.map((step, i) => (
              <li key={i}>
                <Typography variant="body2" style={{ color: colors.text }}>
                  {step}
                </Typography>
              </li>
            ))}
          </ol>
          {info.learnMoreUrl && (
            <Link href={info.learnMoreUrl} target="_blank" rel="noreferrer">
              Learn more
            </Link>
          )}
        </div>
      </Collapse>
    </div>
  )
}

export const ErrorCenter = ({ errors, onPerson, onResolve }: ErrorCenterProps) => {
  const [openErrors, setOpenErrors] = useState<Set<BusinessError>>(new Set())

  const toggleError = (error: BusinessError) => {
    setOpenErrors(prev => {
      const next = new Set(prev)
      if (next.has(error)) {
        next.delete(error)
      } else {
        next.add(error)
      }
      return next
    })
  }

  // the card as a whole still needs to read as urgent at a glance (the old solid-red block did this,
  // if dated) — an accent bar keyed off the worst error present, rather than filling the whole card.
  const hasCritical = errors.some(e => getBusinessErrorInfo(e, onPerson).severity === 'critical')
  const accentColor = hasCritical ? severityColors.critical.border : severityColors.warning.border

  return (
    <Card style={{ marginBottom: 16, borderLeft: `6px solid ${accentColor}` }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ErrorOutlineIcon style={{ color: accentColor }} />
          <Typography variant="h5">Business Error Center</Typography>
        </div>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          We have detected some issues with this business. It is likely due to your Facebook or
          Google Ads configuration. Review the issues below and follow the instructions. You can
          clear an error after following its suggested steps.
        </Typography>
        {onPerson && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            This business is connected by one person's own Facebook login, because none of our
            system users can reach both its Ad Account and Page. It keeps working only while that
            person has access and their login is valid.
          </Typography>
        )}
        {!onPerson && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            This business uses Facebook Builder — a system user (an automated identity, invisible in
            your Facebook Business Manager's user list) holds these connections on our behalf,
            rather than one specific authorized person. Where a fix below says{' '}
            <strong>Reconnect</strong>, that action uses <strong>your own Facebook login</strong>{' '}
            (whoever is currently logged into this browser) to re-request access for the system user
            — it only works if you personally have admin-level access to that asset in Facebook.
          </Typography>
        )}
        <Typography variant="body2" style={{ marginTop: 8 }}>
          <strong>Note: </strong>you must save changes to the business before you can clear the
          errors
        </Typography>
        <Divider style={{ margin: '16px 0' }} />
        {errors.map(error => (
          <ErrorCard
            key={error}
            error={error}
            onPerson={onPerson}
            isOpen={openErrors.has(error)}
            onToggle={() => toggleError(error)}
            onResolve={() => onResolve(error)}
          />
        ))}
      </CardContent>
    </Card>
  )
}
